import PropTypes from "prop-types";
import React, { memo, useEffect, useMemo, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  useAsyncDebounce,
  useExpanded,
  useFilters,
  useGlobalFilter,
  useGroupBy,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { changePayloadToTableState } from "../../common/utils/utils";
import { table_filters } from "../../store/react-table/react-table.selector";
import { COLUMNS } from "./data";
import {
  AscIcon,
  DescIcon,
  TableAscIcon,
  TableBothIcon,
  TableDescIcon,
} from "./icons";
import PaginationBar from "./pagination-bar";
import PageNotFound from "../../views/PageNotFound";
const propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object),
  recordsTotal: PropTypes.number,
  recordsPerPage: PropTypes.number,
  onTableStateChange: PropTypes.func,
  isRowSelectionAvailable: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
  initialState: PropTypes.object,
};

const defaultProps = {};
const ReactTable = ({
  data,
  columns: columns_data,
  recordsTotal,
  recordsPerPage = 10,
  onTableStateChange,
  isRowSelectionAvailable = false,
  onRowSelectionChange,
  initialState = { pageIndex: 0, pageSize: 10, sortBy: [], filters: [] },
  isHeaderShow = true,
  isPaginationShow = true,
  bordered,
  prrntClassName,
  noDataImage,
  noDataText,
  onFallbackClick,
  noDataButtonText,
  contentCenter = false,
  borderBottom = false,
  currentPageNumber,
}) => {
  const tableFilters = useSelector(table_filters);
  const isFirstRender = React.useRef(true);
  const columns = React.useMemo(() => columns_data || COLUMNS, []);
  const { pathname } = useLocation();
  const onFetchDataDebounced = useAsyncDebounce(onTableStateChange, 1000);
  function DefaultColumnFilter({ column }) {
    const { filterValue, preFilteredRows, setFilter } = column;
    const typeHandler =
      column.id === "created_at" || column.id === "modified_on"
        ? "date"
        : "text";

    return (
      <Form.Control
        className="border-0 border-bottom w-100 h-25 rounded-3 mt-2 p-2"
        type={typeHandler}
        value={filterValue || ""}
        onChange={(e) => {
          setFilter(e.target.value || undefined);
        }}
        placeholder={`Search here...`}
      />
    );
  }
  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      disableFilters: false,
      disableSortBy: false,
    }),
    []
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setFilter,
    state,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      manualSortBy: false,
      autoResetSortBy: false,
      disableMultiSort: false,
      manualFilters: true,
      autoResetFilters: false,
      initialState: changePayloadToTableState(initialState),
      manualPagination: true,
      autoResetPage: false,
      pageCount: Math.ceil(recordsTotal / recordsPerPage),
    },
    useFilters,
    useGlobalFilter,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (!isRowSelectionAvailable) return;
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",

          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),

          Cell: ({ row }) => {
            return (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            );
          },
        },
        ...columns,
      ]);
    }
  );

  useEffect(() => {
    if (onTableStateChange && typeof onTableStateChange === "function") {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      onTableStateChange(state);
    }
    return () => {};
  }, [state.pageIndex, state.pageSize, state.sortBy]);

  useEffect(() => {
    if (currentPageNumber !== null && currentPageNumber !== undefined) {
      gotoPage(currentPageNumber);
    }

    return () => {
      gotoPage(0);
      setPageSize(10);
    };
  }, []);

  useEffect(() => {
    if (
      onTableStateChange &&
      typeof onTableStateChange === "function" &&
      (currentPageNumber === null || currentPageNumber === undefined)
    ) {
      if (state?.filters?.length > 0) {
        onFetchDataDebounced(state);
      } else {
        onFetchDataDebounced(state);
      }
    }
  }, [state.filters]);

  useEffect(() => {
    if (onRowSelectionChange && typeof onRowSelectionChange === "function") {
      onRowSelectionChange(state.selectedRowIds);
    }
  }, [state.selectedRowIds]);

  const pageIndex = useMemo(() => state.pageIndex, [state.pageIndex]);
  const pageSize = useMemo(() => state.pageSize, [state.pageSize]);

  const totalPage = useMemo(
    () => Math.ceil(recordsTotal / pageSize),
    [recordsTotal, pageSize]
  );
  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = React.useRef();
      const resolvedRef = ref || defaultRef;

      React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);

      return (
        <>
          <Form.Check type="checkbox" ref={resolvedRef} {...rest} />
        </>
      );
    }
  );

  useEffect(() => {
    if (tableFilters?.length > 0) {
      for (let tableFilter of tableFilters) {
        setFilter(tableFilter.id, tableFilter.value);
      }
    }
  }, [tableFilters]);

  return (
    <>
      <div className={`fancy-table table-responsive rounded ${prrntClassName}`}>
        <Table
          {...getTableProps()}
          className={`mb-0 ${bordered ? "table-bordered" : ""}`}
        >
          {isHeaderShow && (
            <thead>
              {headerGroups.map((headerGroup, index) => {
                return (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={`header-group-${index}`}
                    className={`${borderBottom ? "border-bottom" : ""}`}
                  >
                    {headerGroup.headers.map((column, index) => {
                      return (
                        <th
                          className={`${
                            contentCenter && index !== 0
                              ? "text-center"
                              : "text-start"
                          } align-top`}
                          scope="col"
                          key={`header-${index}`}
                        >
                          <div
                            className={`d-flex ${
                              contentCenter && index !== 0
                                ? "justify-content-center"
                                : "justify-content-start"
                            } align-items-center`}
                            {...column.getHeaderProps(
                              column.getSortByToggleProps()
                            )}
                          >
                            {column.render("Header")}

                            <span>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <div className="ms-1">
                                    <TableDescIcon />
                                  </div>
                                ) : (
                                  <div className="ms-1">
                                    <TableAscIcon />
                                  </div>
                                )
                              ) : (
                                <div className="ms-1">
                                  <TableBothIcon />
                                </div>
                              )}
                            </span>
                          </div>
                          <div>
                            {column.canFilter ? column.render("Filter") : null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
          )}

          {data?.length > 0 ? (
            <tbody {...getTableBodyProps()}>
              {rows.map((row, index) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className={`${borderBottom ? "border-bottom" : ""}`}
                    key={`row-${index}`}
                  >
                    {row.cells.map((cell, index) => {
                      const isMasked =
                        typeof cell?.value === "string" &&
                        cell?.value?.includes("**");
                      return (
                        <td
                          {...cell.getCellProps()}
                          title={!isMasked ? cell?.value : ""}
                          className={` ${
                            contentCenter && index !== 0
                              ? "text-center"
                              : "text-start"
                          } text-wrap max-width`}
                          key={`cell-${index}`}
                          style={{ color: "#525252" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          ) : (
            <></>
          )}
        </Table>
        {(!data || data.length === 0) && (
          <PageNotFound
            title={noDataText}
            backgroundImage={noDataImage}
            onFallbackClick={onFallbackClick}
            buttonText={noDataButtonText}
          />
        )}
      </div>

      {isPaginationShow && (
        <PaginationBar
          gotoPage={gotoPage}
          onNextPage={nextPage}
          onPreviousPage={previousPage}
          totalPage={totalPage}
          pageIndex={pageIndex}
        />
      )}
    </>
  );
};

ReactTable.propTypes = propTypes;
ReactTable.defaultProps = defaultProps;

export default memo(ReactTable);
